import { Injectable } from '@angular/core';

export interface CommissionTier {
  id: string;
  name: string;
  subtitle: string;
  minPrice: number;
  maxPrice: number | null; // null represents infinity / no upper limit
  ratePercent: number;
  description: string;
  features: string[];
  badgeColor: string;
}

export interface FeeCalculation {
  grossPrice: number;
  ratePercent: number;
  feeAmount: number;
  sellerNet: number;
  tier: CommissionTier;
}

@Injectable({
  providedIn: 'root'
})
export class RevenueService {

  public readonly tiers: CommissionTier[] = [
    {
      id: 'tier-entry',
      name: 'Curios & Folk Antiquities',
      subtitle: 'Accessible entry-level collectibles',
      minPrice: 0,
      maxPrice: 249.99,
      ratePercent: 5.0,
      description: 'Lowest platform commission to stimulate discovery and frequent trade of vintage coins, ephemera, and entry-level curios.',
      features: [
        '5.0% flat platform fee',
        'Living Provenance ledger entry',
        'Stripe & Google Pay secure checkout',
        'Instant printable packing slip'
      ],
      badgeColor: '#28a745'
    },
    {
      id: 'tier-fine',
      name: 'Fine Decorative Arts & Silver',
      subtitle: 'Mid-range antique pieces & decorative art',
      minPrice: 250,
      maxPrice: 999.99,
      ratePercent: 8.5,
      description: 'Standard gallery commission for 18th-20th century decorative arts, sterling silver, porcelain, and collector items.',
      features: [
        '8.5% platform commission',
        'Evri & Royal Mail digital QR shipping labels',
        'Integrated buyer & seller dispute protection',
        'Full timeline milestone tracking'
      ],
      badgeColor: '#17a2b8'
    },
    {
      id: 'tier-rare',
      name: 'Rare Horology, Fossils & Jewelry',
      subtitle: 'High-value certified timepieces, gems & fossils',
      minPrice: 1000,
      maxPrice: 4999.99,
      ratePercent: 12.0,
      description: 'Curated commission for valuable Swiss watches, fine estate jewelry, ancient Roman coins, and certified paleontological specimens.',
      features: [
        '12.0% platform commission',
        'Royal Mail Special Delivery (insured to £2,500)',
        'Physical Certificate of Custody generation',
        'High-value transit insurance support',
        'Escrow custody handover protocol'
      ],
      badgeColor: '#b8860b'
    },
    {
      id: 'tier-museum',
      name: 'Masterpieces & Museum Antiquities',
      subtitle: 'Museum-grade historical artifacts & masterworks',
      minPrice: 5000,
      maxPrice: null,
      ratePercent: 15.0,
      description: 'Full-service provenance verification, archival permanence, and white-glove security for premier historical antiquities.',
      features: [
        '15.0% platform commission',
        'White-glove provenance & lineage audit',
        'Archival permanent ledger inscription',
        'Priority specialist custody escrow guarantee',
        'Dedicated curator concierge support'
      ],
      badgeColor: '#6f42c1'
    }
  ];

  constructor() {}

  /**
   * Find the matching fee tier for a given price
   */
  public getTierForPrice(price: number): CommissionTier {
    const validPrice = Math.max(0, Number(price) || 0);
    const tier = this.tiers.find(t => {
      if (t.maxPrice === null) {
        return validPrice >= t.minPrice;
      }
      return validPrice >= t.minPrice && validPrice <= t.maxPrice;
    });

    return tier || this.tiers[0];
  }

  /**
   * Calculate Antiqry platform fee and net seller earnings for any price
   */
  public calculateFee(price: number): FeeCalculation {
    const grossPrice = Math.max(0, Number(price) || 0);
    const tier = this.getTierForPrice(grossPrice);
    const feeAmount = Math.round((grossPrice * (tier.ratePercent / 100)) * 100) / 100;
    const sellerNet = Math.max(0, Math.round((grossPrice - feeAmount) * 100) / 100);

    return {
      grossPrice,
      ratePercent: tier.ratePercent,
      feeAmount,
      sellerNet,
      tier
    };
  }

  /**
   * Calculate aggregate metrics for a collection of completed sales
   */
  public calculateSellerMetrics(sales: any[]): {
    totalGross: number;
    totalNetWon: number;
    totalFeesPaid: number;
    salesCount: number;
    averageSalePrice: number;
  } {
    let totalGross = 0;
    let totalNetWon = 0;
    let totalFeesPaid = 0;
    let salesCount = 0;

    for (const sale of sales) {
      if (!sale || sale.state !== 'completed') continue;
      const price = Number(sale.price) || 0;
      if (price <= 0) continue;

      salesCount++;
      totalGross += price;

      // If fee was already saved on the record, use it; otherwise compute from price
      if (sale.commissionFee !== undefined && sale.sellerNet !== undefined) {
        totalFeesPaid += Number(sale.commissionFee) || 0;
        totalNetWon += Number(sale.sellerNet) || 0;
      } else {
        const calc = this.calculateFee(price);
        totalFeesPaid += calc.feeAmount;
        totalNetWon += calc.sellerNet;
      }
    }

    const averageSalePrice = salesCount > 0 ? Math.round((totalGross / salesCount) * 100) / 100 : 0;

    return {
      totalGross: Math.round(totalGross * 100) / 100,
      totalNetWon: Math.round(totalNetWon * 100) / 100,
      totalFeesPaid: Math.round(totalFeesPaid * 100) / 100,
      salesCount,
      averageSalePrice
    };
  }
}
